import React, { useState, useEffect, SyntheticEvent, useRef } from "react";
import AdminLayout from "../../layout/AdminLayout";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Divider,
  Alert,
  Tabs,
  Tab,
  Typography,
  Grid
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DeleteIcon from "@mui/icons-material/Delete";
import "dayjs/locale/tr";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import AdminField from "../../layout/AdminField";
import {
  initializeNewCampaign,
  initializeCampaignData,
} from "../../utils/initializeCampaign";
import { v4 as uuid } from "uuid";
import agent from "../../api/agent";
import { toast } from "react-toastify";
import ImagePickerModal from "../../layout/ImagePickerModal";
import VideoPickerModal from "../../layout/VideoPickerModal";
import Swal from "sweetalert2";
import { basename } from "../../utils/env";
import CampaignFields from "./main/CampaignFields";
import FormCustomization from "./main/FormCustomization";
import ScreenStyleSettings from "./main/ScreenStyleSettings";
import { Dayjs } from "dayjs";

export default function TouchWinCampaignDetails() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<any | null>(null);
  const [titleInput, setTitleInput] = useState<string>("");
  const [tabValue, setTabValue] = useState(0);
  const [errors, setErrors] = useState<any>({});
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [openVideoModal, setOpenVideoModal] = useState<boolean>(false);
  const [activeVideoField, setActiveVideoField] = useState<string | null>(null);

  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [iframeSize, setIframeSize] = useState({ width: 360, height: 640 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [submitType, setSubmitType] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);

  const navigate = useNavigate();
  const gameScreenUrl = `${basename}/game/${id || campaign?.id}?gameType=${campaign?.gameType || 4}`;

  useEffect(() => {
    if (id) {
      agent.Campaigns.details(id).then((response) => {
        const campaignData: any = response;
        setCampaign(initializeCampaignData(campaignData));
        setTitleInput(campaignData.title);
        console.log(campaignData);
      });
    } else {
      const newCampaign = initializeNewCampaign();
      setCampaign(newCampaign);
      setTitleInput(newCampaign.title);
    }
  }, [id]);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, [submitType]);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFieldChange = (field: string, value: any) => {
    setIsDirty(false);
    setCampaign({ ...campaign, [field]: value });
  };

  const handleScreenStyleChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      screenStyle: { ...campaign.screenStyle, [field]: value },
    });
  };

  const handleTouchWinChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      touchWin: { ...campaign.touchWin, [field]: value },
    });
  };

  const handleFormStateChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      formState: { ...campaign.formState, [field]: value },
    });
  };

  const validateForm = () => {
    let tempErrors: any = {};

    if (!titleInput) tempErrors.titleInput = "Kampanya Başlığı zorunludur";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isDirty) {
      // If there are no changes, don't resubmit or show error
      if (submitType === "regularSubmit") {
        navigate("/campaigns", { state: { reload: true } });
      }
      return;
    }

    const campaignId = campaign?.id || uuid();
    const screenStyleId = campaign?.screenStyle.id || uuid();
    const touchWinId = campaign?.touchWin.id || uuid();
    const formStateId = campaign?.formState.id || uuid();

    const campaignData = {
      ...campaign,
      id: campaignId,
      gameType: 4,
      title: titleInput,
      startDate: (campaign!.startDate as Dayjs).toISOString(),
      endDate: (campaign!.endDate as Dayjs).toISOString(),
      collectUserData:
          campaign!.collectUserData === 1
            ? 1
            : campaign!.collectUserData === 2
              ? 2
              : campaign!.collectUserData === 3
                ? 3
                : 4,
      screenStyle: {
        ...campaign!.screenStyle,
        id: screenStyleId,
        campaignId: campaignId,
      },
      touchWin: {
        ...campaign!.touchWin,
        id: touchWinId,
        campaignId: campaignId,
        tileAmount: campaign!.touchWin.tileAmount === 3 ? 3 : 4,
      },
      formState: {
        ...campaign!.formState,
        id: formStateId,
        campaignId: campaignId,
      },
      wheel: {},
      puzzle: {},
      scratchWin: {},
      matchWin: {},
      quiz: {},
      catchGame: {}
    };

    try {
      if (id) {
        await agent.Campaigns.update(campaignData);
      } else {
        console.log(campaignData);
        await agent.Campaigns.create(campaignData);
      }
      if (submitType === "regularSubmit") {
        navigate("/campaigns", { state: { reload: true } });
      } else {
        setIsDirty(true);
      }
    } catch (error: any) {
      if (error.response) {
        console.error("Error response data:", error.response.data);
      } else {
        console.error("Kampanya düzenlenirken bir hata oluştu:", error.message);
      }
    }
    setSubmitType(null);
  };

  const handleShowGameScreen = async () => {
    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    setSubmitType("showGameScreen");

    if (!isLoaded) {
      setIframeSrc(gameScreenUrl);
      setIsLoaded(true);
    } else {
      setIframeSrc(null);
      setTimeout(() => setIframeSrc(gameScreenUrl), 100);
    }
  };

  const handleRegularSubmit = async () => {
    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    setSubmitType("regularSubmit");
  };

  const openImagePicker = (field: string) => {
    setActiveField(field);
    setOpenModal(true);
  };

  const openVideoPicker = (field: string) => {
    setActiveVideoField(field);
    setOpenVideoModal(true);
  };

  const handleImageSelect = (url: string) => {
    if (!campaign || !activeField) return;

    if (["modalBg", "modalInnerBg"].includes(activeField)) {
      setCampaign({ ...campaign, [activeField]: url });
    } else if (["logo", "screenBg", "screenCenterBg"].includes(activeField)) {
      handleScreenStyleChange(activeField, url);
    } else {
      handleTouchWinChange(activeField, url);
    }

    setOpenModal(false);
  };

  const handleVideoSelect = (url: string) => {
    if (!campaign || !activeVideoField) return;
    setCampaign({ ...campaign, [activeVideoField]: url });
    setOpenVideoModal(false);
  };

  const handleDeleteGameData = () => {
    Swal.fire({
      title: "Silmek istediğinizden emin misiniz?",
      text: "Bu kampanya için kaydedilmiş tüm oyun ve oyuncu verileri silinecektir. Silme işlemini geri alamazsınız!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Tamam",
      cancelButtonText: "İptal",
    }).then((result) => {
      if (result.isConfirmed) {
        agent.Players.deleteAll(id)
          .then(() => {
            toast.success("Tüm oyun ve oyuncu verileri silindi!");
          })
          .catch((error) => {
            toast.error("Veriler silinirken bir hata oluştu.");
          });
      }
    });
  };

  const handleSizeChange = (size: string | number, shouldStartPreview = false) => {
    const parsedSize = Number(size);
    if (!Number.isNaN(parsedSize)) {
      const isLandscape = [1, 3, 5].includes(parsedSize);
      setIframeSize(isLandscape ? { width: 640, height: 360 } : { width: 360, height: 640 });

      if (shouldStartPreview && id) {
        handleShowGameScreen();
      }
      return;
    }

    if (size === "9:16") {
      setIframeSize({ width: 360, height: 640 });
    } else if (size === "16:9") {
      setIframeSize({ width: 640, height: 360 });
    }
  };

  if (!campaign) return null;

  return (
    <AdminLayout title={campaign.title || "Dokun Kazan Kampanyası Ekle"}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/campaigns")}
        sx={{ mr: 3 }}
      >
        Kampanyalara Dön
      </Button>
      {id && (
        <>
          <Button
            variant="contained"
            startIcon={<EmojiEventsIcon />}
            color="secondary"
            onClick={() => navigate(`/scoreboard/${id}?campaignTitle=${campaign.title}&gameType=${campaign.gameType}`)}
            sx={{ mr: 2 }}
          >
            Skor Tablosu
          </Button>
          <Button
            variant="contained"
            startIcon={<AnalyticsIcon />}
            color="success"
            onClick={() => navigate(`/statistics/${id}?campaignTitle=${campaign.title}&gameType=${campaign.gameType}`)}
            sx={{ mr: 2 }}
          >
            İstatistikler
          </Button>
          <Button
            variant="contained"
            startIcon={<VideogameAssetIcon />}
            color="warning"
            onClick={() => window.open(gameScreenUrl, "_blank")}
            sx={{ mr: 2 }}
          >
            Oyun Ekranı
          </Button>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            color="error"
            onClick={handleDeleteGameData}
          >
            Oyun ve Oyuncu Verilerini Sil
          </Button>
        </>
      )}
      {campaign.id ? (
        <Alert sx={{ my: 3 }} severity="warning">
          Kampanyanızı oluşturdunuz. Artık <strong>Çark Dilim Alanları</strong>{" "}
          sekmesindeki <strong>Dilim Sayısı</strong> oluşturulmuş kampanya
          içinde değiştirilemez.
        </Alert>
      ) : (
        <Alert sx={{ my: 3 }} severity="info">
          Temel kampanya bilgilerinizi girdikten sonra kaydetmeden önce lütfen
          yan sekmelerdeki bilgileri de düzenleyiniz. Kampanyalarınızı oluşturduktan sonra ödül
          istatistiklerini inceleyebileceğiniz <strong>İstatistikler</strong>{" "}
          butonu hemen yukarıda yer alacaktır.
        </Alert>
      )}
      <Tabs
        value={tabValue}
        className="admin-tabs"
        onChange={handleTabChange}
        aria-label="basic tabs example"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab
          label="Kampanya Alanları"
          id="simple-tab-0"
          aria-controls="simple-tabpanel-0"
        />
        <Tab
          label="Genel Görselleştirme"
          id="simple-tab-1"
          aria-controls="simple-tabpanel-1"
        />
        <Tab
          label="Dokun Kazan Alanları"
          id="simple-tab-2"
          aria-controls="simple-tabpanel-2"
        />
        <Tab
          label="Form Özelleştirme"
          id="simple-tab-3"
          aria-controls="simple-tabpanel-3"
        />
      </Tabs>
      <Divider sx={{ mb: 5 }} />
      <form ref={formRef} onSubmit={handleSubmit}>
        <CampaignFields
          campaign={campaign}
          setCampaign={setCampaign}
          errors={errors}
          setErrors={setErrors}
          titleInput={titleInput}
          setTitleInput={setTitleInput}
          handleFieldChange={handleFieldChange}
          openImagePicker={openImagePicker}
          openVideoPicker={openVideoPicker}
          tabValue={tabValue}
        />
        <ScreenStyleSettings
          tabValue={tabValue}
          tabOrder={1}
          campaign={campaign}
          handleScreenStyleChange={handleScreenStyleChange}
          handleSizeChange={handleSizeChange}
          handleShowGameScreen={handleShowGameScreen}
          openImagePicker={openImagePicker}
          id={id}
          iframeSrc={iframeSrc}
          isLoaded={isLoaded}
          iframeSize={iframeSize}
        />
        {campaign.touchWin && (
          <>
            <div
              role="tabpanel"
              hidden={tabValue !== 2}
              id="simple-tabpanel-2"
              aria-labelledby="simple-tab-2"
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 5 }}>
                Dokun Kazan Ekranı Ayarları
              </Typography>

              <Divider sx={{ mb: 5 }} />

              <Grid container spacing={6} sx={{ mb: 4 }}>
                <Grid item xs={6}>
                  <Grid container spacing={4} sx={{ mb: 4 }}>
                    <AdminField
                      label="Slot Görseli"
                      value={campaign.touchWin.slotImage}
                      onChange={(value) =>
                        handleTouchWinChange("slotImage", value)
                      }
                      helperText="Çıkan objelerin altındaki görselini ekleyiniz. (Örnek: basket potası) Oyunun başlaması için görsel eklemeniz zorunludur"
                      type="button"
                      buttonText="Galeri'den Seç"
                      onClick={() => openImagePicker("slotImage")}
                      error={!!errors.slotImage}
                      errorMessage={errors.slotImage}
                    />
                    <AdminField
                      label="Obje Görseli"
                      value={campaign.touchWin.objectImage}
                      onChange={(value) =>
                        handleTouchWinChange("objectImage", value)
                      }
                      helperText="Çıkan obje görselini ekleyiniz. (Örnek: basket topu) Oyunun başlaması için görsel eklemeniz zorunludur"
                      type="button"
                      buttonText="Galeri'den Seç"
                      onClick={() => openImagePicker("objectImage")}
                      error={!!errors.objectImage}
                      errorMessage={errors.objectImage}
                    />                
                    <AdminField
                      label="Oyun Süresi"
                      value={campaign.touchWin.gameDuration}
                      type="number"
                      onChange={(value) => handleTouchWinChange("gameDuration", value)}
                      helperText="Oyunu kazanmak için kaç saniye verileceğini rakam olarak giriniz"
                    />
                    <AdminField
                      label="Puan Miktarı"
                      value={campaign.touchWin.pointPerTouch}
                      type="number"
                      onChange={(value) => handleTouchWinChange("pointPerTouch", value)}
                      helperText="Başarılı dokunma başına verilen puan miktarını rakam olarak giriniz"
                    />
                    <AdminField
                      label="Dokun Kazan Slot Adedi"
                      type="radio"
                      value={campaign.touchWin.tileAmount}
                      onChange={(value) =>
                        handleTouchWinChange("tileAmount", parseInt(value))
                      }
                      helperText="Oyunun kaça kaç slotlarda oluşacağını seçiniz"
                      selectOptions={[
                        { label: "3x3", value: 3 },
                        { label: "3x4", value: 4 },
                      ]}
                    />
                  </Grid>
                </Grid>                
              </Grid>
            </div>
          </>
        )}
        <FormCustomization
          tabValue={tabValue}
          tabOrder={3}
          campaign={campaign}
          handleFormStateChange={handleFormStateChange}
        />
        <Divider />
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleRegularSubmit}
          sx={{ my: 4 }}
        >
          Kaydet
        </Button>
      </form>
      <ImagePickerModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSelect={handleImageSelect}
      />
      <VideoPickerModal
        open={openVideoModal}
        onClose={() => setOpenVideoModal(false)}
        onSelect={handleVideoSelect}
      />
    </AdminLayout>
  );
}
